import { useState } from 'react';

import { PartActionSelection, SelectedPartType, SelectedSerial } from './MobilePartActionsDialog.props';
import {
  PartTypeActionLiveSearchItem,
  PartTypeActionLiveSearchItemCategoryCode,
} from '../../../../../../@types/partType.type';
import { ServiceObject } from '../../../../../../@types/service.type';
import { useSearchPartsOrPartTypes } from '../../../../../../queries/live-search-query';

export const usePartActionSearch = (
  service: ServiceObject,
  selectedAction: PartActionSelection | null
) => {
  const [outSerial, setOutSerial] = useState<SelectedSerial | null>(null);
  const [inSerial, setInSerial] = useState<SelectedSerial | null>(null);
  const [selectedPartType, setSelectedPartType] = useState<SelectedPartType | null>(null);
  const [partTypeDefaults, setPartTypeDefaults] = useState<PartTypeActionLiveSearchItem[]>([]);
  const [defaultsLoadedFor, setDefaultsLoadedFor] = useState<string | null>(null);
  const searchInPartsMutation = useSearchPartsOrPartTypes();
  const searchOutPartsMutation = useSearchPartsOrPartTypes();
  const searchPartsMutation = useSearchPartsOrPartTypes();

  const getSearchParams = (searchName: string, isOut: boolean) => ({
    name: searchName,
    serialiseType: '',
    partTypeId: '',
    clientContractIds: [service.clientId || 0, service.contractId || 0],
    locationId: service.mainLocationNodeId || 0,
    allowedPartLocationType: 'node',
    partActionType: isOut ? 'remove' : 'install',
    searchType: 'part-and-part-type',
    serviceTypeCode: service.serviceTypeCode || '',
  });

  const loadPartTypeDefaults = async (actionCode: string): Promise<void> => {
    if (defaultsLoadedFor === actionCode) return;
    try {
      // Map action codes to API-compatible params for the preFill endpoint.
      // DOA is functionally a remove; Cleaned uses consumable part types.
      // The preFill endpoint only supports standard action types (install, remove, etc.).
      const isClean = actionCode === 'clean';
      const serialiseType = isClean ? 'consumable' : 'serialised';
      const partActionType = actionCode === 'doa' ? 'remove' : isClean ? 'install' : actionCode;
      const data = await searchPartsMutation.mutateAsync({
        name: '',
        serialiseType,
        partTypeId: '',
        clientContractIds: [service.clientId || 0, service.contractId || 0],
        locationId: service.mainLocationNodeId || 0,
        allowedPartLocationType: 'node',
        partActionType,
        searchType: 'part-type',
        preFill: true,
      });
      setPartTypeDefaults(data);
      setDefaultsLoadedFor(actionCode);
    } catch (err) {
      console.error(
        'Failed to load default part types:',
        err instanceof Error ? err.message : 'Unknown error'
      );
      setDefaultsLoadedFor(actionCode);
    }
  };

  const handleSearch = async (
    searchValue: string,
    isOut: boolean
  ): Promise<PartTypeActionLiveSearchItem[]> => {
    if (!searchValue || searchValue.length < 2 || !selectedAction) return [];
    try {
      const params = getSearchParams(searchValue, isOut);
      const mutation = isOut ? searchOutPartsMutation : searchInPartsMutation;
      const results = await mutation.mutateAsync(params);
      return results.filter(
        (r) => r.categoryCode === PartTypeActionLiveSearchItemCategoryCode.Serialised && r.partId
      );
    } catch (err) {
      console.error('Search failed:', err instanceof Error ? err.message : 'Unknown error');
      return [];
    }
  };

  const makeSerial = (item: PartTypeActionLiveSearchItem): SelectedSerial => ({
    partId: item.partId || 0,
    serial: item.serial1,
    partTypeName: item.partTypeName,
    fullData: item,
  });

  const buildBulkPartData = (serial: SelectedSerial | null, id: number, actionTypeCode: string) => {
    if (!serial) return {};
    const isSerialisedPart =
      serial.fullData.categoryCode === PartTypeActionLiveSearchItemCategoryCode.Serialised;
    return {
      id,
      partTypeId: serial.fullData.partTypeId,
      name: serial.fullData.partTypeName,
      sw: serial.fullData.softwareVersion || null,
      hw: serial.fullData.hardwareVersion || null,
      fw: serial.fullData.firmwareVersion || null,
      ft: serial.fullData.fleetTagName || null,
      serial: serial.serial,
      partId: serial.partId,
      ns_partId: serial.fullData.ns_partId || null,
      qty: isSerialisedPart ? 1 : null,
      category: serial.fullData.categoryCode,
      condition: serial.fullData.conditionCode || null,
      location: serial.fullData.locationName || null,
      notes: '',
      type: actionTypeCode,
      images: [],
    };
  };

  const buildPartTypePartData = (
    partType: SelectedPartType | null,
    id: number,
    actionTypeCode: string
  ): Record<string, unknown> => {
    if (!partType) return {};
    return {
      id,
      partTypeId: partType.partTypeId,
      name: partType.partTypeName,
      type: actionTypeCode,
      qty: 1,
      category: 'serialised',
      images: [],
    };
  };

  const reset = () => {
    setOutSerial(null);
    setInSerial(null);
    setSelectedPartType(null);
    setPartTypeDefaults([]);
    setDefaultsLoadedFor(null);
  };

  return {
    outSerial,
    inSerial,
    selectedPartType,
    partTypeDefaults,
    setOutSerial,
    setInSerial,
    setSelectedPartType,
    loadPartTypeDefaults,
    handleSearch,
    makeSerial,
    buildBulkPartData,
    buildPartTypePartData,
    isSearching:
      searchInPartsMutation.isPending ||
      searchOutPartsMutation.isPending ||
      searchPartsMutation.isPending,
    reset,
  };
};
